import React from "react";
import {
  SendOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
  CheckCircleOutlined,
  FileSearchOutlined,
  Loading3QuartersOutlined,
} from "@ant-design/icons";
import { useCurrentApplication } from "../../../Context/CurrentApplicationContext/CurrentApplicationContext";

interface ProcessStep {
  key: "apply" | "profile-match" | "assessment" | "interview" | "result";
  title: string;
  icon: React.ReactNode;
  isCompleted: boolean;
  isDisabled: boolean;
}

const ApplicationJourneySteps: React.FC = () => {
  const {
    jobPost,
    rounds,
    jobApplication,
    handleViewChange,
    interviewSessions,
  } = useCurrentApplication();

  if (!jobPost) {
    return null;
  }

  const handleStepClick = (
    step: "apply" | "profile-match" | "assessment" | "interview" | "result",
  ) => {
    handleViewChange(step);
  };

  const isAssessment = rounds.length > 0;
  const isOnlineInterview = jobPost && jobPost.onlineInterview;

  const isApplied = !!jobApplication;

  const isProfileMatchingDone = Boolean(
    jobApplication &&
    jobApplication.profileMatchStatus &&
    jobApplication.profileId,
  );

  const isAssessmentDone =
    interviewSessions.length > 0 &&
    interviewSessions.every((s) => s.status !== "pending");

  const isOnlineInterviewDone = Boolean(
    jobApplication?.status !== "applied" && jobApplication?.interviewSchedule,
  );

  const isEnableApplyStep = true;
  const isEnableProfileMatchStep =
    !!jobApplication?.profileId && !!jobApplication?.profileMatchStatus;

  const isEnableAssessmentStep =
    rounds.length > 0 && interviewSessions.length > 0;

  const isEnableOnlineInterviewStep = Boolean(
    (jobPost.onlineInterview && jobApplication?.interviewSchedule) ||
    jobApplication?.status === "interview",
  );
  const isFinalStage = Boolean(
    (jobApplication && jobApplication?.status === "rejected") ||
    jobApplication?.status === "hired" ||
    jobApplication?.status === "shortlisted",
  );
  const isPartiallyQualified = Boolean(
    jobApplication?.status === "applied" &&
    jobApplication?.questionnaireStatus === "partially_qualified",
  );

  const isActionRequired = Boolean(
    jobApplication?.status === "applied" &&
    jobApplication?.questionnaireStatus === "action_required",
  );
  const isEnableResultStep =
    isFinalStage || isPartiallyQualified || isActionRequired;

  const steps: ProcessStep[] = [
    {
      key: "apply",
      title: "Apply",
      icon: <SendOutlined />,
      isCompleted: isApplied,
      isDisabled: !isEnableApplyStep,
    },
    {
      key: "profile-match",
      title: "Profile Fit",
      icon: <FileSearchOutlined />,
      isCompleted: isProfileMatchingDone,
      isDisabled: !isEnableProfileMatchStep,
    },
  ];

  if (isAssessment) {
    steps.push({
      key: "assessment",
      title: "AI Assessment",
      icon: <FileTextOutlined />,
      isCompleted:
        isAssessmentDone ||
        (jobApplication?.status !== "applied" &&
          jobApplication?.questionnaireStatus === "pending"),
      isDisabled: !isEnableAssessmentStep,
    });
  }

  if (isOnlineInterview) {
    steps.push({
      key: "interview",
      title: "Interview",
      icon: <VideoCameraOutlined />,
      isCompleted: isOnlineInterviewDone,
      isDisabled: !isEnableOnlineInterviewStep,
    });
  }

  steps.push({
    key: "result",
    title: "Result",
    icon: <CheckCircleOutlined />,
    isCompleted: isEnableResultStep,
    isDisabled: !isEnableResultStep,
  });

  return (
    <div className="job-posts-details__breadcrumb-wrap">
      <div className="job-posts-details__roadmap-wrap">
        <div className={`job-posts-details__roadmap-line`}>
          {steps.map((step) => (
            <div
              onClick={
                step.isDisabled ? undefined : () => handleStepClick(step.key)
              }
              key={step.key}
              className={`job-posts-details__roadmap-step ${step.isCompleted ? "job-posts-details__roadmap-step--active" : ""} ${step.isDisabled ? "job-posts-details__roadmap-step--disabled" : ""}`}
            >
              <div
                className={`job-posts-details__roadmap-dot ${step.isCompleted ? "job-posts-details__roadmap-dot--active" : ""}`}
              >
                {step.icon}
              </div>
              <div className="job-posts-details__roadmap-label">
                <p
                  className={`job-posts-details__roadmap-title ${step.isCompleted ? "job-posts-details__roadmap-title--active" : ""}`}
                >
                  {/* {!step.isDisabled && !step.isCompleted && (
                    <Loading3QuartersOutlined spin />
                  )}{" "} */}
                  {step.title}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ApplicationJourneySteps;
